import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import ReactApexChart from "react-apexcharts";
import { fetchLabUtilizationRanking } from "../../../API_Service/Dashboard_Slice";
import { companyID, DashboardCard, EmptyState, ChartSkeleton, InlineAlert } from "../../../Common/UIComponents";
import { useDashboardSignalR } from "../../..//Common/useDashboardSignalR";
import { CHART_PALETTE, dataLabelStyle } from "../../../Common/chartTheme";

// The shared palette (8 colors), cycled below via `% BAR_COLORS.length` for
// a ranked list of up to 10 labs, replacing the previous unrelated rainbow.
const BAR_COLORS = CHART_PALETTE;

const MONTHS = [
  { label: "January", value: 0 },
  { label: "February", value: 1 },
  { label: "March", value: 2 },
  { label: "April", value: 3 },
  { label: "May", value: 4 },
  { label: "June", value: 5 },
  { label: "July", value: 6 },
  { label: "August", value: 7 },
  { label: "September", value: 8 },
  { label: "October", value: 9 },
  { label: "November", value: 10 },
  { label: "December", value: 11 },
];

function getYearOptions(span = 5) {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let i = 0; i < span; i++) years.push(currentYear - i);
  return years;
}

function getMonthDateRange(month, year) {
  const today = new Date();
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);

  const isCurrentMonth =
    year === today.getFullYear() && month === today.getMonth();

  const fromDate = first.toISOString().split("T")[0];
  const toDate = (isCurrentMonth ? today : last).toISOString().split("T")[0];

  return { fromDate, toDate };
}

export default function LabUtilizationRanking({ companyId = companyID }) {
  const dispatch = useDispatch();

  const today = new Date();
  const [selectedMonth, setSelectedMonth] = useState(today.getMonth());
  const [selectedYear, setSelectedYear] = useState(today.getFullYear());
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const yearOptions = getYearOptions(5);

  const loadRankingData = useCallback(() => {
    const { fromDate, toDate } = getMonthDateRange(selectedMonth, selectedYear);
    setLoading(true);
    setError(null);

    dispatch(fetchLabUtilizationRanking({ companyId: companyID, fromDate, toDate }))
      .unwrap()
      .then((result) => {
        if (Array.isArray(result)) setData(result);
        else if (Array.isArray(result?.data)) setData(result.data);
        else if (Array.isArray(result?.$values)) setData(result.$values);
        else setData([]);
      })
      .catch((err) => setError(err?.message || "Failed to load data."))
      .finally(() => setLoading(false));
  }, [dispatch, selectedMonth, selectedYear]);

  useEffect(() => {
    loadRankingData();
  }, [loadRankingData]);

  useDashboardSignalR(loadRankingData);

  // Sort descending by UtilizationPct, take top 10
  const sorted = Array.isArray(data)
    ? [...data].sort((a, b) => b.UtilizationPct - a.UtilizationPct).slice(0, 10)
    : [];

  const categories = sorted.map((d) => String(d.FacilityName));
  const seriesData = sorted.map((d) => parseFloat(d.UtilizationPct) || 0);
  const colors = sorted.map((_, i) => BAR_COLORS[i % BAR_COLORS.length]);

  const chartOptions = {
    chart: {
      type: "bar",
      toolbar: {
        show: true,
        tools: {
          download: true,
          selection: false,
          zoom: false,
          zoomin: false,
          zoomout: false,
          pan: false,
          reset: false,
        },
        export: {
          csv: {
            filename: "Lab_Utilization_Ranking",
            columnDelimiter: ",",
            headerCategory: "Facility",
            headerValue: "Utilization %",
          },
          svg: { filename: "Lab_Utilization_Ranking" },
          png: { filename: "Lab_Utilization_Ranking" },
        },
      },
      animations: { enabled: true, speed: 600 },
      fontFamily: "'Inter', 'Segoe UI', sans-serif",
    },
    plotOptions: {
      bar: {
        horizontal: true,
        distributed: true,
        barHeight: "60%",
        dataLabels: { position: "end" },
        borderRadius: 6,
      },
    },
    colors: colors,
    dataLabels: {
      enabled: true,
      textAnchor: "end",
      style: { ...dataLabelStyle, colors: ["#1f2937"] },
      formatter: function (val) {
        return val.toFixed(1) + "%";
      },
      offsetX: 10,
      dropShadow: { enabled: false },
    },
    stroke: {
      width: 0,
      colors: ["transparent"],
    },
    xaxis: {
      categories: categories.length > 0 ? categories : ["No Data"],
      min: 0,
      max: 100,
      labels: {
        formatter: (val) =>
          val !== undefined && val !== "No Data" ? val + "%" : "",
        style: { fontSize: "11px", fontWeight: 500, colors: "#64748b" },
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: {
        show: true,
        style: {
          fontSize: "13px",
          fontWeight: 600,
          colors: "#1f2937",
        },
        maxWidth: 150,
        offsetX: -10,
      },
    },
    grid: {
      borderColor: "transparent",
      xaxis: { lines: { show: true, strokeDashArray: 4 } },
      yaxis: { lines: { show: false } },
      padding: { top: 0, right: 20, bottom: 0, left: 20 },
    },
    legend: { show: false },
    tooltip: {
      enabled: true,
      theme: "light",
      x: { show: true },
      y: {
        title: { formatter: () => "" },
        formatter: (val, { dataPointIndex }) => {
          const lab = sorted[dataPointIndex];
          return [
            `<strong style="color: #1f2937; font-size: 13px;">Utilization: ${val.toFixed(1)}%</strong>`,
            `<span style="color: #475569; font-size: 12px;">Total: ${lab?.TotalHours ?? "-"} hrs</span>`,
            `<span style="color: #475569; font-size: 12px;">Billable: ${lab?.BillableHours ?? "-"} hrs</span>`,
            `<span style="color: #475569; font-size: 12px;">Non-Billable: ${lab?.NonBillableHours ?? "-"} hrs</span>`,
          ].join("<br/>");
        },
      },
      style: {
        fontSize: "12px",
        backgroundColor: "#ffffff",
        borderColor: "#e5e7eb",
        borderRadius: "8px",
        padding: "8px 12px",
      },
      onDatasetHover: { highlightDataSeries: true },
    },
  };

  const chartSeries = [{ name: "Utilization", data: seriesData }];

  return (
    <DashboardCard
      icon="leaderboard"
      title="Laboratory Utilization Ranking"
      subtitle="Top labs by utilization percentage"
      actions={
        <div className="flex gap-2">
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(Number(e.target.value))}
            className="px-3 py-1.5 border border-slate-200 rounded-md text-xs font-medium text-slate-700 bg-white hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-[#08757C]/50 transition-all"
          >
            {MONTHS.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="px-3 py-1.5 border border-slate-200 rounded-md text-xs font-medium text-slate-700 bg-white hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-[#08757C]/50 transition-all"
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
      }
    >
      {/* Body */}
      {loading ? (
        <ChartSkeleton height={256} />
      ) : error ? (
        <InlineAlert type="error" message="Failed to load data. Please try again." />
      ) : sorted.length === 0 ? (
        <EmptyState icon="bar_chart" message="No utilization data found for this period." />
      ) : (
        <>
          {/* Summary Pills */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
            {sorted.slice(0, 3).map((lab, i) => (
              <div
                key={lab.FacilityId}
                className="flex items-center gap-3 p-3 rounded-lg border bg-white hover:shadow-sm transition-all"
                style={{ borderColor: BAR_COLORS[i] }}
              >
                <div
                  className="flex items-center justify-center h-9 w-9 rounded-full text-white font-bold text-sm flex-shrink-0"
                  style={{ backgroundColor: BAR_COLORS[i] }}
                >
                  #{i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">{lab.FacilityName}</p>
                  <p className="text-xs text-slate-500 mt-0.5">Utilization</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-lg font-bold" style={{ color: BAR_COLORS[i] }}>
                    {lab.UtilizationPct}%
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Chart */}
          {sorted.length > 0 && categories.length > 0 && (
            <div className="chart-fade-in mb-4 overflow-visible">
              <ReactApexChart
                key={categories.join(",")}
                options={chartOptions}
                series={chartSeries}
                type="bar"
                height={Math.max(380, sorted.length * 48)}
              />
            </div>
          )}

          {/* Footer Legend */}
          <div className="pt-4 border-t border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: CHART_PALETTE[2] }}></div>
              <span className="text-sm text-slate-600">Billable hours counted toward utilization</span>
            </div>
            <div className="text-sm text-slate-600">
              Total Labs: <span className="font-bold text-slate-900">{sorted.length}</span>
            </div>
          </div>
        </>
      )}
    </DashboardCard>
  );
}